import React, { useState, useEffect } from 'react';
import { createTicket } from '../../api/ticketApi';
import { getAllAgents } from '../../api/agentApi.js';
import './CreateTicketModal.css';

export default function CreateTicketModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    user_query: '',
    priority: 'Medium',
    status: 'Open',
    assigned_to: '',
    initial_message: '',
    userId: '',
    appId: ''
  });

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAgents();
    }
  }, [isOpen]);

  const fetchAgents = async () => {
    try {
      const response = await getAllAgents('manager');
      setAgents(response.agents || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_query.trim()) {
      setError('Query is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await createTicket(formData);
      
      if (response.success) {
        onSuccess && onSuccess(response.ticket);
        handleClose();
      } else {
        setError(response.msg || 'Failed to create ticket');
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Error creating ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      user_query: '',
      priority: 'Medium',
      status: 'Open',
      assigned_to: '',
      initial_message: '',
      userId: '',
      appId: ''
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Ticket</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              <span>⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="user_query">Query / Issue *</label>
            <textarea
              id="user_query"
              name="user_query"
              value={formData.user_query}
              onChange={handleChange}
              placeholder="Describe the issue or query..."
              rows="5"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="assigned_to">Assign To Agent</label>
            <select
              id="assigned_to"
              name="assigned_to"
              value={formData.assigned_to}
              onChange={handleChange}
            >
              <option value="">-- Unassigned --</option>
              {agents
                .filter(agent => agent.is_active)
                .map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} - Current: {agent.current_chats}/{agent.max_concurrent_chats} tickets
                    {agent.status === 'Online' ? ' 🟢' : agent.status === 'Away' ? ' 🟡' : ' ⚫'}
                  </option>
                ))}
            </select>
            <small className="form-hint">
              Select an agent to assign this ticket. Shows current workload.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="initial_message">Initial Message / Notes (Optional)</label>
            <textarea
              id="initial_message"
              name="initial_message"
              value={formData.initial_message}
              onChange={handleChange}
              placeholder="Add any additional notes or context..."
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="userId">User ID (Optional)</label>
              <input
                type="text"
                id="userId"
                name="userId"
                value={formData.userId}
                onChange={handleChange}
                placeholder="Enter user ID"
              />
            </div>

            <div className="form-group">
              <label htmlFor="appId">App ID (Optional)</label>
              <input
                type="text"
                id="appId"
                name="appId"
                value={formData.appId}
                onChange={handleChange}
                placeholder="Enter app ID"
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
